/**
 * Cart Button Manager
 * Instantly disables all cart action buttons when a cart operation is in progress
 * to prevent rapid-fire requests and race conditions.
 */

class CartButtonManager {
  constructor() {
    this.isProcessing = false;
    this.disabledButtons = new Set();
    this.disabledInputs = new Set();
  }

  /**
   * Disable all cart action buttons instantly
   */
  disableAllButtons() {
    if (this.isProcessing) return;
    this.isProcessing = true;

    // Selectors for all cart action buttons
    const buttonSelectors = [
      // Product form submit buttons
      '[type="submit"][form*="product"]',
      'product-form [type="submit"]',
      '.product-form__submit',
      'button[data-add-to-cart]',
      '[name="add"]',
      
      // Quantity buttons on product cards and quick order lists
      '.quantity__button',
      'quantity-input button',
      'button[name="plus"]',
      'button[name="minus"]',
      
      // Quick quantity buttons on product cards
      '.quick-qty-buttons button',
      '.qty-btn',
      
      // Quick order list buttons
      '.quick-order-list button',
      '.quick-order-list-remove-button',
      
      // Cart update buttons
      '[name="update"]',
      '.cart__update-button',
      '.cart__checkout-button'
    ];

    // Disable all matching buttons
    buttonSelectors.forEach(selector => {
      const buttons = document.querySelectorAll(selector);
      buttons.forEach(button => {
        if (!button.disabled) {
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('cart-processing');
          this.disabledButtons.add(button);
        }
      });
    });

    // Disable quantity inputs
    const inputSelectors = [
      '.quantity__input',
      '.cart-item__quantity input',
      'quantity-input input',
      'input[data-quantity-variant-id]'
    ];

    inputSelectors.forEach(selector => {
      const inputs = document.querySelectorAll(selector);
      inputs.forEach(input => {
        if (!input.disabled) {
          input.disabled = true;
          input.classList.add('cart-processing');
          this.disabledInputs.add(input);
        }
      });
    });
  }

  /**
   * Re-enable all disabled buttons
   */
  enableAllButtons() {
    this.isProcessing = false;

    // Re-enable buttons
    this.disabledButtons.forEach(button => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      button.classList.remove('cart-processing');
    });
    this.disabledButtons.clear();

    // Re-enable inputs
    this.disabledInputs.forEach(input => {
      input.disabled = false;
      input.classList.remove('cart-processing');
    });
    this.disabledInputs.clear();
  }

  /**
   * Check if cart is currently processing
   * @returns {boolean}
   */
  getProcessing() {
    return this.isProcessing;
  }
}

// Create a singleton instance
if (!window.cartButtonManager) {
  window.cartButtonManager = new CartButtonManager();
}

// Export for module systems if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartButtonManager;
}
